import React, { useState } from 'react';
import { Input } from './ui/input';
import { CalendarDays, Pencil, RotateCcw } from 'lucide-react';
import { DecoupageEditor } from './DecoupageEditor';

export const PALETTE = [
  { bg: 'bg-indigo-500', light: 'bg-indigo-100 text-indigo-700', border: 'border-indigo-300' },
  { bg: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-300' },
  { bg: 'bg-amber-500', light: 'bg-amber-100 text-amber-700', border: 'border-amber-300' },
  { bg: 'bg-rose-500', light: 'bg-rose-100 text-rose-700', border: 'border-rose-300' },
  { bg: 'bg-sky-500', light: 'bg-sky-100 text-sky-700', border: 'border-sky-300' },
  { bg: 'bg-violet-500', light: 'bg-violet-100 text-violet-700', border: 'border-violet-300' },
  { bg: 'bg-orange-500', light: 'bg-orange-100 text-orange-700', border: 'border-orange-300' },
  { bg: 'bg-teal-500', light: 'bg-teal-100 text-teal-700', border: 'border-teal-300' },
];

export const fmtH = (h) => (Number.isInteger(h) ? h : Math.round(h * 100) / 100);

export const buildAutoDays = (mods, hpj) => {
  const days = [];
  let current = { segments: [] };
  let total = 0;
  mods.forEach((mf) => {
    let remaining = mf.duree_heures || 0;
    while (remaining > 0) {
      const take = Math.min(hpj - total, remaining);
      if (take > 0) {
        current.segments.push({ module_id: mf.module_id, heures: take });
        total += take;
        remaining -= take;
      }
      if (total >= hpj) {
        days.push(current);
        current = { segments: [] };
        total = 0;
      }
    }
  });
  if (current.segments.length > 0) days.push(current);
  return days;
};

const AutoBars = ({ days, hpj, modulesFormation, getModuleInfo }) => {
  const colorIdx = Object.fromEntries(modulesFormation.map((mf, i) => [mf.module_id, i % PALETTE.length]));
  return (
    <div className="p-3 space-y-1.5 max-h-[300px] overflow-y-auto">
      {days.map((day, i) => {
        const total = day.segments.reduce((s, x) => s + x.heures, 0);
        const incomplete = total < hpj;
        let prevModule = null;
        return (
          <div key={i} className="flex items-center gap-2" data-testid={`jour-${i + 1}`}>
            <span className="text-[11px] font-bold text-slate-500 w-8 shrink-0">J{i + 1}</span>
            <div className="flex-1 flex h-8 rounded-md overflow-hidden border border-slate-200 bg-slate-50">
              {day.segments.map((seg, j) => {
                const mod = getModuleInfo(seg.module_id);
                const color = PALETTE[colorIdx[seg.module_id] ?? 0];
                const suite = i > 0 && j === 0 && days[i - 1].segments.slice(-1)[0]?.module_id === seg.module_id;
                prevModule = seg.module_id;
                return (
                  <div
                    key={j}
                    className={`${color.bg} flex items-center justify-center px-1 min-w-0 border-r border-white/40 last:border-r-0`}
                    style={{ width: `${(seg.heures / Math.max(hpj, total)) * 100}%` }}
                    title={`${mod.code} — ${mod.titre}${suite ? ' (suite)' : ''} : ${fmtH(seg.heures)}h`}
                  >
                    <span className="text-[9px] font-bold text-white truncate">
                      <span className="font-mono">{mod.code}{suite ? '…' : ''}</span> {mod.titre} · {fmtH(seg.heures)}h
                    </span>
                  </div>
                );
              })}
              {incomplete && (
                <div className="flex items-center justify-center px-1" style={{ width: `${((hpj - total) / hpj) * 100}%` }}>
                  <span className="text-[9px] text-slate-400">{fmtH(hpj - total)}h libre</span>
                </div>
              )}
            </div>
            <span className={`text-[10px] font-medium w-8 text-right shrink-0 ${total > hpj ? 'text-red-600' : incomplete ? 'text-amber-600' : 'text-slate-500'}`}>
              {fmtH(total)}h
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const DecoupageJournees = ({ modulesFormation, getModuleInfo, heuresParJour = 7, decoupage = null, onChange }) => {
  const [localHpj, setLocalHpj] = useState(heuresParJour);
  const editable = typeof onChange === 'function';
  const hpjRaw = editable ? heuresParJour : localHpj;
  const hpj = Math.max(1, parseFloat(hpjRaw) || 7);
  const totalHeures = modulesFormation.reduce((s, mf) => s + (mf.duree_heures || 0), 0);
  const isCustom = Array.isArray(decoupage);
  const days = isCustom ? decoupage : buildAutoDays(modulesFormation, hpj);

  if (totalHeures === 0) return null;

  const setHpj = (v) => (editable ? onChange({ heures_par_jour: v }) : setLocalHpj(v));

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="decoupage-journees">
      <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-semibold text-slate-700">Découpage par journée</span>
          <span className="text-xs text-slate-400">
            {fmtH(totalHeures)}h → {days.length} jour{days.length > 1 ? 's' : ''}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isCustom ? 'bg-violet-100 text-violet-700' : 'bg-slate-200 text-slate-600'}`} data-testid="decoupage-mode">
            {isCustom ? 'Personnalisé' : 'Automatique'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-500">Journée type</span>
            <Input
              type="number"
              value={hpjRaw}
              onChange={(e) => setHpj(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="h-6 w-14 text-[11px] text-center"
              min="1"
              max="12"
              step="0.5"
              data-testid="input-heures-par-jour"
            />
            <span className="text-[11px] text-slate-500">h</span>
          </div>
          {editable && !isCustom && (
            <button
              type="button"
              onClick={() => onChange({ decoupage_journees: buildAutoDays(modulesFormation, hpj) })}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100"
              data-testid="btn-personnaliser-decoupage"
            >
              <Pencil className="w-3 h-3" /> Personnaliser
            </button>
          )}
          {editable && isCustom && (
            <button
              type="button"
              onClick={() => onChange({ decoupage_journees: null })}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-100"
              data-testid="btn-reset-decoupage"
            >
              <RotateCcw className="w-3 h-3" /> Réinitialiser (auto)
            </button>
          )}
        </div>
      </div>

      {editable && isCustom ? (
        <DecoupageEditor
          days={days}
          hpj={hpj}
          modulesFormation={modulesFormation}
          getModuleInfo={getModuleInfo}
          onChange={(d) => onChange({ decoupage_journees: d })}
        />
      ) : (
        <AutoBars days={days} hpj={hpj} modulesFormation={modulesFormation} getModuleInfo={getModuleInfo} />
      )}

      <div className="px-3 pb-2 flex flex-wrap gap-1">
        {modulesFormation.map((mf, idx) => {
          const mod = getModuleInfo(mf.module_id);
          const color = PALETTE[idx % PALETTE.length];
          return (
            <span key={mf.module_id} className={`px-1.5 py-0.5 rounded text-[9px] font-medium max-w-[220px] truncate ${color.light}`} title={`${mod.code} — ${mod.titre}`}>
              <span className="font-mono">{mod.code}</span> {mod.titre} · {fmtH(mf.duree_heures)}h
            </span>
          );
        })}
      </div>
    </div>
  );
};
